import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, Trash2, FileUp, Download, Loader2, Globe, KeyRound, Copy, Mail, RotateCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface Props { clienteId: string; }

const CATEGORIAS = ['informe', 'contrato', 'certificado', 'factura', 'otro'];

function genPassword(len = 12) {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
  let out = '';
  const arr = new Uint32Array(len);
  crypto.getRandomValues(arr);
  for (let i = 0; i < len; i++) out += chars[arr[i] % chars.length];
  return out;
}

export const PortalClienteTab: React.FC<Props> = ({ clienteId }) => {
  const { empresa } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [docs, setDocs] = useState<any[]>([]);
  const [config, setConfig] = useState<any>({ activo: true, mensaje_bienvenida: '' });
  const [loading, setLoading] = useState(true);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [docOpen, setDocOpen] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [form, setForm] = useState({ nombre: '', email: '', cargo: '', password: genPassword() });
  const [docForm, setDocForm] = useState({ titulo: '', descripcion: '', categoria: 'otro', file: null as File | null });
  const [credencialesCreadas, setCredencialesCreadas] = useState<{ email: string; password: string } | null>(null);

  async function load() {
    setLoading(true);
    const [u, d, c] = await Promise.all([
      (supabase as any).from('usuarios_portal_cliente').select('*').eq('cliente_id', clienteId).order('created_at', { ascending: false }),
      (supabase as any).from('portal_documentos').select('*').eq('cliente_id', clienteId).order('created_at', { ascending: false }),
      (supabase as any).from('cliente_portal_config').select('*').eq('cliente_id', clienteId).maybeSingle(),
    ]);
    setUsers(u.data ?? []);
    setDocs(d.data ?? []);
    if (c.data) setConfig(c.data);
    setLoading(false);
  }

  useEffect(() => { load(); }, [clienteId]);

  async function invitar() {
    if (!form.nombre || !form.email || form.password.length < 8) {
      return toast.error('Nombre, email y contraseña (mín 8 chars) son obligatorios');
    }
    setInviting(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const r = await fetch(`${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/portal-invite-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sess.session?.access_token}`,
          apikey: (import.meta as any).env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ cliente_id: clienteId, ...form, portal_url: portalUrl }),
      });
      const payload = await r.json();
      if (!r.ok) throw new Error(payload?.error || 'No se pudo invitar');
      toast.success('Usuario creado e invitación enviada por email');
      setCredencialesCreadas({ email: form.email, password: form.password });
      setInviteOpen(false);
      setForm({ nombre: '', email: '', cargo: '', password: genPassword() });
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setInviting(false);
    }
  }

  async function reenviarInvitacion(u: any) {
    if (!confirm(`¿Reenviar invitación a ${u.email}? Se generará una nueva contraseña.`)) return;
    try {
      const { data: sess } = await supabase.auth.getSession();
      const r = await fetch(`${(import.meta as any).env.VITE_SUPABASE_URL}/functions/v1/portal-invite-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sess.session?.access_token}`,
          apikey: (import.meta as any).env.VITE_SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({ resend: true, user_id: u.user_id, portal_url: portalUrl }),
      });
      const payload = await r.json();
      if (!r.ok) throw new Error(payload?.error || 'Error');
      toast.success('Invitación reenviada por email');
    } catch (e: any) {
      toast.error(e.message);
    }
  }

  async function resetearPassword(u: any) {
    if (!confirm(`¿Restablecer la contraseña de ${u.email}? Se enviará por email.`)) return;
    try {
      const { data, error } = await supabase.functions.invoke('portal-reset-password-admin', {
        body: { portal_user_id: u.id, portal_url: portalUrl },
      });
      if (error || (data as any)?.error) throw new Error((data as any)?.error || error?.message);
      toast.success('Contraseña restablecida y enviada por email');
    } catch (e: any) {
      toast.error(e.message);
    }
  }


  async function toggleActivo(u: any) {
    await (supabase as any).from('usuarios_portal_cliente').update({ activo: !u.activo }).eq('id', u.id);
    load();
  }

  async function eliminarUsuario(u: any) {
    if (!confirm(`¿Eliminar acceso de ${u.nombre}?`)) return;
    await (supabase as any).from('usuarios_portal_cliente').delete().eq('id', u.id);
    toast.success('Usuario eliminado');
    load();
  }

  async function subirDoc() {
    if (!docForm.file || !docForm.titulo) return toast.error('Título y archivo obligatorios');
    if (!empresa?.id) return toast.error('Empresa no detectada');
    setUploading(true);
    try {
      const path = `${empresa.id}/${clienteId}/${Date.now()}-${docForm.file.name}`;
      const { error: upErr } = await supabase.storage.from('portal-documentos').upload(path, docForm.file);
      if (upErr) throw upErr;
      const { error: insErr } = await (supabase as any).from('portal_documentos').insert({
        cliente_id: clienteId,
        empresa_id: empresa.id,
        titulo: docForm.titulo,
        descripcion: docForm.descripcion || null,
        categoria: docForm.categoria,
        file_path: path,
        file_name: docForm.file.name,
        file_size: docForm.file.size,
        mime_type: docForm.file.type,
      });
      if (insErr) throw insErr;
      toast.success('Documento subido');
      setDocOpen(false);
      setDocForm({ titulo: '', descripcion: '', categoria: 'otro', file: null });
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  }

  async function eliminarDoc(d: any) {
    if (!confirm(`¿Eliminar "${d.titulo}"?`)) return;
    await supabase.storage.from('portal-documentos').remove([d.file_path]);
    await (supabase as any).from('portal_documentos').delete().eq('id', d.id);
    toast.success('Documento eliminado');
    load();
  }

  async function descargar(path: string) {
    const { data, error } = await supabase.storage.from('portal-documentos').createSignedUrl(path, 60);
    if (error || !data) return toast.error('Error');
    window.open(data.signedUrl, '_blank');
  }

  async function saveConfig(activo: boolean) {
    if (!empresa?.id) return;
    const payload = { cliente_id: clienteId, empresa_id: empresa.id, activo, mensaje_bienvenida: config.mensaje_bienvenida || null };
    await (supabase as any).from('cliente_portal_config').upsert(payload, { onConflict: 'cliente_id' });
    setConfig({ ...config, activo });
    toast.success(activo ? 'Portal activado' : 'Portal desactivado');
  }

  const portalUrl = `${window.location.origin}/portal/login${empresa?.id ? `?empresa=${empresa.id}` : ''}`;

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      {/* Configuración */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Globe className="h-4 w-4" /> Configuración del portal</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Portal activo</p>
              <p className="text-xs text-muted-foreground">Si está desactivado, los usuarios no podrán entrar.</p>
            </div>
            <Switch checked={config.activo} onCheckedChange={saveConfig} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">URL del portal</Label>
            <div className="flex gap-2">
              <Input readOnly value={portalUrl} className="text-xs" />
              <Button variant="outline" size="icon" onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success('Copiado'); }}>
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Credenciales recién creadas */}
      {credencialesCreadas && (
        <Card className="border-primary">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4" /> Credenciales creadas</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p className="text-muted-foreground text-xs">Guárdalas y compártelas con el cliente. No volverás a verlas.</p>
            <div className="bg-muted p-3 rounded font-mono text-xs space-y-1">
              <div><strong>URL:</strong> {portalUrl}</div>
              <div><strong>Email:</strong> {credencialesCreadas.email}</div>
              <div><strong>Contraseña:</strong> {credencialesCreadas.password}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => {
              navigator.clipboard.writeText(`Portal: ${portalUrl}\nEmail: ${credencialesCreadas.email}\nContraseña: ${credencialesCreadas.password}`);
              toast.success('Copiado al portapapeles');
            }}>
              <Copy className="h-4 w-4 mr-2" /> Copiar todo
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setCredencialesCreadas(null)}>Cerrar</Button>
          </CardContent>
        </Card>
      )}

      {/* Usuarios */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Usuarios del portal ({users.length})</CardTitle>
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button size="sm"><UserPlus className="h-4 w-4 mr-2" /> Invitar</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuevo usuario del portal</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nombre*</Label><Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} /></div>
                <div><Label>Email*</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Cargo</Label><Input value={form.cargo} onChange={e => setForm({ ...form, cargo: e.target.value })} placeholder="Responsable, Gerente..." /></div>
                <div>
                  <Label>Contraseña inicial*</Label>
                  <div className="flex gap-2">
                    <Input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                    <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, password: genPassword() })}>Generar</Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">El cliente podrá cambiarla al entrar.</p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={invitar} disabled={inviting}>
                  {inviting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Crear usuario
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-center text-muted-foreground text-sm py-6">Sin usuarios aún.</p>
          ) : (
            <div className="space-y-2">
              {users.map(u => (
                <div key={u.id} className="flex items-center justify-between p-3 border rounded-md gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm">{u.nombre}</p>
                      {u.cargo && <Badge variant="outline" className="text-xs">{u.cargo}</Badge>}
                      {!u.activo && <Badge variant="destructive" className="text-xs">Inactivo</Badge>}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    {u.ultimo_acceso && (
                      <p className="text-xs text-muted-foreground">
                        Último acceso: {format(new Date(u.ultimo_acceso), "d MMM yyyy HH:mm", { locale: es })}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0 items-center">
                    <Switch checked={u.activo} onCheckedChange={() => toggleActivo(u)} />
                    <Button variant="ghost" size="icon" title="Reenviar invitación" onClick={() => reenviarInvitacion(u)}>
                      <Mail className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" title="Restablecer contraseña" onClick={() => resetearPassword(u)}>
                      <RotateCw className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => eliminarUsuario(u)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Documentos */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Documentos compartidos ({docs.length})</CardTitle>
          <Dialog open={docOpen} onOpenChange={setDocOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline"><FileUp className="h-4 w-4 mr-2" /> Subir</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Subir documento al portal</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Título*</Label><Input value={docForm.titulo} onChange={e => setDocForm({ ...docForm, titulo: e.target.value })} /></div>
                <div><Label>Descripción</Label><Textarea value={docForm.descripcion} onChange={e => setDocForm({ ...docForm, descripcion: e.target.value })} rows={2} /></div>
                <div>
                  <Label>Categoría</Label>
                  <Select value={docForm.categoria} onValueChange={v => setDocForm({ ...docForm, categoria: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIAS.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Archivo*</Label><Input type="file" onChange={e => setDocForm({ ...docForm, file: e.target.files?.[0] || null })} /></div>
              </div>
              <DialogFooter>
                <Button onClick={subirDoc} disabled={uploading}>
                  {uploading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  Subir
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {docs.length === 0 ? (
            <p className="text-center text-muted-foreground text-sm py-6">Sin documentos compartidos.</p>
          ) : (
            <div className="space-y-2">
              {docs.map(d => (
                <div key={d.id} className="flex items-center justify-between p-3 border rounded-md gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm truncate">{d.titulo}</p>
                      <Badge variant="outline" className="text-xs capitalize">{d.categoria}</Badge>
                    </div>
                    {d.descripcion && <p className="text-xs text-muted-foreground line-clamp-1">{d.descripcion}</p>}
                    <p className="text-xs text-muted-foreground">{format(new Date(d.created_at), "d MMM yyyy", { locale: es })}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => descargar(d.file_path)}><Download className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => eliminarDoc(d)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
